<template>
  <div class="domain-connection-page">
    <!-- Header Section -->
    <div class="card mb-2">
      <div class="card-header">
        <h2>Connect Your Domain</h2>
        <p class="text-muted">
          Enter your domain name to connect with your business application
        </p>
      </div>
      <div class="card-body">
        <div class="">
          <label for="domain">Enter domain name</label>
          <input
            id="domain"
            v-model="domainName"
            placeholder="www.shoptopia.com"
            type="text"
            class="form-control domain-input"
          />
          <small class="text-muted"
            >Enter the domain you want to connect to your store</small
          >
        </div>
        <div
          class="mt-3 d-flex justify-content-center justify-content-sm-start"
        >
          <button
            :disabled="!domainName"
            @click="handleNext"
            class="btn btn-primary next-button"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- DNS Records Section -->
    <div v-if="showDnsConfig">
      <!-- Success Alert -->
      <b-alert
        v-model="showCopyAlert"
        variant="success"
        dismissible
        fade
        class="copy-alert"
      >
        {{ copyMessage }}
      </b-alert>

      <div class="card">
        <div class="card-header">
          <h3>Connect with your existing Domain</h3>
          <p class="text-muted">
            Update these DNS records at your domain provider
          </p>
        </div>
        <div class="card-body">
          <b-table
            :items="dnsRecords"
            :fields="tableFields"
            bordered
            small
            thead-class="header"
            responsive="sm"
            :dark="dark"
            :hover="hover"
            :striped="striped"
            :small="small"
            :fixed="fixed"
          >
            <template #cell(Type)="row">
              <div class="d-flex align-items-center">
                <span class="mr-2">{{ row.item.Type }}</span>
                <b-icon
                  class="cursor-pointer ml-2"
                  @click="copyToClipboard(row.item.Type)"
                  font-scale="1.2"
                  icon="files"
                ></b-icon>
              </div>
            </template>

            <template #cell(Name)="row">
              <div class="d-flex align-items-center">
                <span class="mr-2">{{ row.item.Name }}</span>
                <b-icon
                  class="cursor-pointer ml-2"
                  @click="copyToClipboard(row.item.Name)"
                  font-scale="1.2"
                  icon="files"
                ></b-icon>
              </div>
            </template>

            <template #cell(Value)="row">
              <div class="d-flex align-items-center">
                <span class="mr-2">{{ row.item.Value }}</span>

                <b-icon
                  class="cursor-pointer ml-2"
                  @click="copyToClipboard(row.item.Value)"
                  font-scale="1.2"
                  icon="files"
                ></b-icon>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="alert alert-warning">
        <b-icon icon="exclamation-triangle-fill"></b-icon>
        <strong> Note: </strong> Before clicking verify, please login to your
        domain provider (like GoDaddy/Namecheap) and add the A and CNAME records
        exactly as shown here. Save the changes, then click “I updated my DNS.”
        DNS changes may take up to 48 hours to propagate globally. If you need
        more help, please watch the detailed video.
      </div>
      <!-- Help Section -->
      <div class="card">
        <div class="card-body d-flex">
          <div class="flex-fill">
            <h4>Need help with configuration?</h4>
            <p class="text-muted">
              Watch our step-by-step video guide on how to configure DNS
              settings for your domain
            </p>
            <button class="btn btn-primary">
              <b-icon icon="play-fill" class="mr-2"></b-icon> Watch Tutorial
            </button>
          </div>
          <div class="help-video-placeholder">
            <div class="video-overlay">
              <b-icon icon="play-fill" font-scale="2" variant="light"></b-icon>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="danger" class="mr-2 submit-button">Cancel</b-button>
        <b-button
          variant="primary"
          class="ml-2 submit-button"
          @click="dnsUpdateMethod()"
          >I Updated My DNS</b-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "DomainConfiguration",
  data() {
    return {
      hover: true,
      striped: false,
      bordered: true,
      small: false,
      fixed: false,
      dark: false,
      domainName: "",
      showDnsConfig: false,
      showCopyAlert: false, // controls the copy success alert
      copyMessage: "",
      tableFields: [
        { key: "Type", label: "Type" },
        { key: "Name", label: "Name" },
        { key: "Value", label: "Value" },
      ],
      dnsRecords: [],
    };
  },
  computed: {
    ...mapState({
      extendedList: (state) => state.tenant.extendedList,
    }),
  },
  methods: {
    copyToClipboard(text) {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      this.copyMessage = "Copied to clipboard!";
      this.showCopyAlert = true;

      // Auto-hide after 2 seconds
      setTimeout(() => {
        this.showCopyAlert = false;
      }, 2000);
    },
    async handleNext() {
      const payload = {
        domain: this.domainName,
      };
      try {
        const res = await this.$store.dispatch(
          "profile/mapDomainAction",
          payload
        );
        if (res && res.status && res.status === 1 && res.data.sslValidation) {
          this.$store.dispatch("tenant/setExtendedAttributeList", {});
        } else {
          this.$swal({
            title: "Error",
            type: "error",
            text: res.message || "Failed to map domain",
          });
          return;
        }
      } catch (error) {
        console.error("API Error:", error);
        this.$swal({
          title: "Something went wrong!",
          type: "error",
          text: "Error",
        });
      }
    },
    dnsUpdateMethod() {
      //
    },
  },
  watch: {
    extendedList: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.dnsConfig) {
          const dnsConfig = newVal.dnsConfig;
          // set domainName if present
          if (dnsConfig.domainName) {
            this.domainName = dnsConfig.domainName;
          }

          // set dnsRecords if validationRecords exist
          if (
            dnsConfig.validationRecords &&
            Array.isArray(dnsConfig.validationRecords) &&
            dnsConfig.validationRecords.length > 0
          ) {
            this.dnsRecords = dnsConfig.validationRecords;
            this.showDnsConfig = true; // also show DNS table
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.domain-input {
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
}

.submit-button {
  min-width: 200px;
  max-width: 300px;
  font-size: 14px;
  padding: 6px 18px;
  box-shadow: none;
}

.domain-connection-page {
  background: #f8f9fa;
  padding: 15px;
  min-height: auto;
  box-sizing: border-box;
}

.help-video-placeholder {
  width: 200px;
  max-width: 40%;
  height: 120px;
  background: #333;
  position: relative;
  border-radius: 8px;
}

.video-overlay {
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.next-button {
  display: inline-block;
  width: auto;
  min-width: 120px;
  max-width: 120px;
  padding: 6px 14px;
  white-space: nowrap;
}
.mt-3 {
  margin-top: 1rem;
}
@media (max-width: 575.98px) {
  .mt-3.d-flex {
    justify-content: center !important;
  }
}
.copy-alert {
  position: fixed;
  top: 60px;
  right: 20px;
  min-width: 250px;
  z-index: 1050;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  max-width: calc(100% - 40px);
  overflow-wrap: break-word;
}
</style>
